import React from 'react';
import { Container, Typography, Box, Paper, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import { DESIGN_TOKENS } from '../theme/designTokens';

const PageContainer = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(DESIGN_TOKENS.spacing.section.lg),
  paddingBottom: theme.spacing(DESIGN_TOKENS.spacing.section.lg),
  background: 'linear-gradient(135deg, #FEFEFE 0%, #F9FAFB 100%)',
  minHeight: '100vh',
  [theme.breakpoints.down('md')]: {
    paddingTop: theme.spacing(DESIGN_TOKENS.spacing.section.md),
    paddingBottom: theme.spacing(DESIGN_TOKENS.spacing.section.md),
  },
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(DESIGN_TOKENS.spacing.section.sm),
    paddingBottom: theme.spacing(DESIGN_TOKENS.spacing.section.sm),
  },
}));

const PricingCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  background: '#FEFEFE',
  border: '2px solid #E5E7EB',
  borderRadius: DESIGN_TOKENS.borderRadius.lg,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  '&:hover': {
    transform: 'translateY(-8px)',
    borderColor: '#4338CA',
    boxShadow: '0 12px 24px rgba(67, 56, 202, 0.15)',
  },
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(4),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
}));

const FeaturedCard = styled(PricingCard)(({ theme }) => ({
  borderColor: '#4338CA',
  borderWidth: '3px',
  background: 'linear-gradient(135deg, #FEFEFE 0%, #F9FAFB 100%)',
  '&::before': {
    content: '"POPULAR"',
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),
    background: 'linear-gradient(135deg, #4338CA 0%, #6D28D9 100%)',
    color: '#ffffff',
    padding: theme.spacing(0.5, 1.5),
    borderRadius: DESIGN_TOKENS.borderRadius.sm,
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.05em',
  },
}));

const PricingGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: theme.spacing(4),
  marginTop: theme.spacing(8),
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: theme.spacing(3),
    marginTop: theme.spacing(6),
  },
}));

const PriceAmount = styled(Typography)(({ theme }) => ({
  fontSize: '3rem',
  fontWeight: 700,
  color: '#111827',
  marginBottom: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    fontSize: '2.5rem',
  },
}));


const Pricing = () => {
  const { t } = useTranslation();
  
  // Set page title for analytics
  usePageTitle('Pricing');

  const plans = t('pricing.plans', { returnObjects: true }) as Array<{
    name: string;
    price: string;
    period: string;
    description: string;
    features: string[];
    cta: string;
    featured?: boolean;
  }>;

  return (
    <PageContainer>
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Typography 
            variant="h1" 
            component="h1" 
            align="center" 
            gutterBottom
            sx={{
              fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem', lg: '4rem' },
              fontWeight: 700,
              background: 'linear-gradient(135deg, #111827 0%, #374151 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: 3,
            }}
          >
            {t('pricing.title')}
          </Typography>
          <Typography 
            variant="h2" 
            align="center" 
            paragraph
            sx={{
              color: '#6B7280',
              fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
              lineHeight: 1.6,
              maxWidth: '800px',
              margin: '0 auto',
            }}
          >
            {t('pricing.subtitle')}
          </Typography>
        </motion.div>

        <PricingGrid>
          {plans.map((plan, index) => {
            const CardComponent = plan.featured ? FeaturedCard : PricingCard;
            return (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <CardComponent>
                  <Typography 
                    variant="h3" 
                    component="h2"
                    sx={{ 
                      mb: 2,
                      fontWeight: 600,
                      color: plan.featured ? '#4338CA' : '#111827',
                    }}
                  >
                    {plan.name}
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <PriceAmount>{plan.price}</PriceAmount>
                    <Typography variant="body2" color="text.secondary">
                      {plan.period}
                    </Typography>
                  </Box>
                  <Typography 
                    variant="body1" 
                    color="text.secondary"
                    sx={{ mb: 3, minHeight: '3rem' }}
                  >
                    {plan.description}
                  </Typography>
                  <Box component="ul" sx={{ listStyle: 'none', p: 0, mt: 4, mb: 4 }}>
                    {plan.features.map((feature, idx) => (
                      <Box component="li" key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <CheckCircleIcon sx={{ color: '#10B981', mr: 1.5, mt: 0.25, flexShrink: 0 }} />
                        <Typography variant="body2">{feature}</Typography>
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ mt: 'auto', pt: 3 }}>
                    <Button
                      component={RouterLink}
                      to="/upload"
                      variant={plan.featured ? 'contained' : 'outlined'}
                      fullWidth
                      sx={{
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        ...(plan.featured && {
                          background: 'linear-gradient(135deg, #4338CA 0%, #6D28D9 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #3730A3 0%, #5B21B6 100%)',
                          },
                        }),
                      }}
                    >
                      {plan.cta}
                    </Button>
                  </Box>
                </CardComponent>
              </motion.div>
            );
          })}
        </PricingGrid>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Box
            sx={{
              mt: 8,
              textAlign: 'center',
              p: 4,
              background: '#FEFEFE',
              border: '1px solid #E5E7EB',
              borderRadius: DESIGN_TOKENS.borderRadius.lg,
            }}
          >
            <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
              {t('pricing.faq.title')}
            </Typography>
            <Box sx={{ maxWidth: '800px', mx: 'auto', textAlign: 'left' }}>
              {(t('pricing.faq.items', { returnObjects: true }) as Array<{ question: string; answer: string }>).map((item, idx) => (
                <Box key={idx} sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    {item.question}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.answer}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </motion.div>
      </Container>
    </PageContainer>
  );
};

export default Pricing;

